import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getISOWeek, getISOWeekYear } from 'date-fns';
import { LuCalendarPlus, LuCheck, LuCircleCheck } from 'react-icons/lu';
import { API_BASE_URL } from '../../config';
import { cx } from '../../utils/cx.js';
import { periodOfIsoWeek } from '../../utils/reportPeriod.js';
import { formatParisRange, formatWeekRangeLong, weeksInYear } from '../../utils/weekFormat.js';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import SegmentedControl from '../ui/SegmentedControl';
import { withJail } from './jail.js';

const FIRST_YEAR = 2025;

/**
 * Picks the ISO week of a new report on a grid: existing weeks are ticked, the current and
 * future weeks are locked (their data is not complete yet). Redirects to the report once created.
 */
const CreateReportModal = ({ isOpen, onClose, onCreated, clientSlug, fetchWithAuth, existingReports = [], jail = null, reportDay = 1, reportHour = 0 }) => {
  const navigate = useNavigate();
  const today = new Date();
  const currentYear = getISOWeekYear(today);
  const currentWeek = getISOWeek(today);

  const [year, setYear] = useState(currentYear);
  const [week, setWeek] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createdReportId, setCreatedReportId] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(2);
  const gridRef = useRef(null);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = FIRST_YEAR; y <= currentYear; y++) years.push(y);
    return years;
  }, [currentYear]);

  const existingByYear = useMemo(() => {
    const map = new Map();
    existingReports.forEach((r) => {
      const y = parseInt(r.year, 10);
      if (!map.has(y)) map.set(y, new Set());
      map.get(y).add(parseInt(r.week_number, 10));
    });
    return map;
  }, [existingReports]);

  const isLocked = (w, y) => y > currentYear || (y === currentYear && w >= currentWeek);
  const exists = (w, y) => existingByYear.get(y)?.has(w) || false;
  const isAvailable = (w, y) => !exists(w, y) && !isLocked(w, y);

  // Most recent week that can still be created in a given year.
  const lastAvailableWeek = (y) => {
    for (let w = weeksInYear(y); w >= 1; w--) if (isAvailable(w, y)) return w;
    return null;
  };

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setCreatedReportId(null);
    setRedirectCountdown(2);
    const inCurrentYear = lastAvailableWeek(currentYear);
    if (inCurrentYear || currentYear <= FIRST_YEAR) {
      setYear(currentYear);
      setWeek(inCurrentYear);
    } else {
      setYear(currentYear - 1);
      setWeek(lastAvailableWeek(currentYear - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const changeYear = (y) => {
    setYear(y);
    if (!week || !isAvailable(week, y)) setWeek(lastAvailableWeek(y));
  };

  useEffect(() => {
    if (!createdReportId) return undefined;
    if (redirectCountdown <= 0) {
      navigate(withJail(`/w/${clientSlug}/${createdReportId}`, jail));
      onClose();
      return undefined;
    }
    const timer = setTimeout(() => setRedirectCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [createdReportId, redirectCountdown, navigate, clientSlug, onClose, jail]);

  const handleSubmit = async () => {
    if (!week || !isAvailable(week, year)) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/weekly/client/${clientSlug}/week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, week_number: String(week) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.message || 'Erreur lors de la création du rapport');
        return;
      }
      onCreated?.(data);
      setCreatedReportId(data.id);
    } catch (err) {
      setError('Erreur lors de la création du rapport');
    } finally {
      setIsLoading(false);
    }
  };

  const total = weeksInYear(year);
  const busy = isLoading || !!createdReportId;
  const availableWeeks = Array.from({ length: total }, (_, i) => i + 1).filter((w) => isAvailable(w, year));
  const tabStop = availableWeeks.includes(week) ? week : availableWeeks[0];

  // Radio group keyboard model: one Tab stop, arrows move between the weeks that can be created.
  const onGridKeyDown = (e) => {
    const index = availableWeeks.indexOf(week);
    const moves = {
      ArrowRight: index + 1, ArrowDown: index + 1, ArrowLeft: index - 1, ArrowUp: index - 1, Home: 0, End: availableWeeks.length - 1,
    };
    if (!(e.key in moves) || availableWeeks.length === 0) return;
    e.preventDefault();
    const next = availableWeeks[Math.max(0, Math.min(availableWeeks.length - 1, moves[e.key]))];
    setWeek(next);
    gridRef.current?.querySelector(`[data-week="${next}"]`)?.focus();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      dismissible={!busy}
      icon={LuCalendarPlus}
      title="Nouveau rapport"
      subtitle="Choisissez la semaine : messages et photos seront récupérés puis analysés automatiquement."
      size="md"
      footer={!createdReportId && (
        <>
          <Button onClick={onClose} disabled={isLoading}>Annuler</Button>
          <Button variant="primary" icon={LuCalendarPlus} onClick={handleSubmit} loading={isLoading} disabled={!week || !isAvailable(week, year)}>
            {week ? `Créer la semaine ${week}` : 'Créer le rapport'}
          </Button>
        </>
      )}
    >
      {createdReportId ? (
        <div className="flex flex-col items-center py-8 text-center" role="status">
          <span className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e2f5ea] text-[#16a866] dark:bg-green-900/30 dark:text-green-300">
            <LuCircleCheck className="h-7 w-7" aria-hidden />
          </span>
          <p className="text-base font-semibold text-gray-900 dark:text-white">Rapport créé</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Ouverture du rapport dans {redirectCountdown} seconde{redirectCountdown > 1 ? 's' : ''}…
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {error && <Alert tone="danger">{error}</Alert>}

          {yearOptions.length > 1 && (
            <SegmentedControl
              size="sm"
              ariaLabel="Année"
              value={year}
              onChange={changeYear}
              options={yearOptions.map((y) => ({ value: y, label: String(y) }))}
            />
          )}

          <div ref={gridRef} role="radiogroup" aria-label={`Semaines ${year}`} onKeyDown={onGridKeyDown} className="grid grid-cols-7 gap-1.5 sm:grid-cols-9">
            {Array.from({ length: total }, (_, i) => i + 1).map((w) => {
              const done = exists(w, year);
              const locked = isLocked(w, year);
              const selected = week === w;
              const reason = done ? 'Rapport existant' : locked ? (w === currentWeek && year === currentYear ? 'Semaine en cours' : 'Semaine à venir') : `Semaine ${w}`;
              return (
                <button
                  key={w}
                  type="button"
                  role="radio"
                  data-week={w}
                  tabIndex={w === tabStop ? 0 : -1}
                  aria-checked={selected}
                  aria-label={`Semaine ${w} — ${reason}`}
                  title={reason}
                  disabled={done || locked || isLoading}
                  onClick={() => setWeek(w)}
                  className={cx(
                    'relative flex h-10 items-center justify-center rounded-lg text-sm font-semibold tabular-nums outline-none transition-colors',
                    'focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-night-800',
                    selected && 'bg-brand-600 text-white shadow-sm',
                    !selected && done && 'cursor-not-allowed bg-[#e2f5ea] text-[#15784c] dark:bg-green-900/30 dark:text-green-300',
                    !selected && !done && locked && 'cursor-not-allowed border border-dashed border-gray-200 text-gray-300 dark:border-gray-800 dark:text-gray-600',
                    !selected && !done && !locked && 'cursor-pointer border border-gray-200 bg-white text-gray-700 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-gray-800 dark:bg-night-850 dark:text-gray-200 dark:hover:border-brand-700 dark:hover:bg-brand-950',
                  )}
                >
                  {w}
                  {done && <LuCheck className="absolute right-1 top-1 h-2.5 w-2.5" aria-hidden />}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-brand-600" aria-hidden />Sélectionnée</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#bfe7cf]" aria-hidden />Déjà créée</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm border border-dashed border-gray-300" aria-hidden />En cours ou à venir</span>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-800 dark:bg-night-850">
            {week ? (
              <>
                <p className="font-semibold text-gray-900 dark:text-white">Semaine {week} · {year}</p>
                <p className="text-gray-500 dark:text-gray-400">
                  {reportDay === 1 && reportHour === 0
                    ? formatWeekRangeLong(year, week)
                    : formatParisRange(periodOfIsoWeek(year, week, reportDay, reportHour))}
                </p>
              </>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">Toutes les semaines passées de {year} ont déjà un rapport.</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CreateReportModal;
